import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { apiService } from "../services/apiService";
import { VCOnboardingData } from "../types/vcOnboarding";
import { PortfolioCompany } from "../types/portfolio";
import VCSidebar from "../components/vc/VCSidebar";
import VCPortfolioPage from "../components/vc/VCPortfolioPage";
import VCUpdatesPage from "../components/vc/VCUpdatesPage";
import styles from "./VCDashboard.module.css";

const PORTFOLIO_POLL_INTERVAL_MS = 60_000;

const VCDashboard: React.FC = () => {
  const [vcData, setVcData] = useState<VCOnboardingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [portfolioCompanies, setPortfolioCompanies] = useState<PortfolioCompany[]>([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState<string | null>(null);

  useEffect(() => {
    loadVCData();
    loadPortfolio();
  }, []);

  const loadVCData = async () => {
    try {
      const response = await apiService.get("/api/vc/onboarding");
      if (response.data.success && response.data.data) {
        setVcData(response.data.data);
      }
    } catch (error) {
      console.error("Error loading VC data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    setPortfolioError(null);
    try {
      const response = await apiService.getVCPortfolio();
      if (response.success) {
        setPortfolioCompanies(response.data);
      }
    } catch (err: any) {
      setPortfolioError(err.message || "Failed to load portfolio");
      console.error("Error loading portfolio:", err);
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  // Poll portfolio every 60 seconds to reflect new company sign-ups without requiring a page reload
  useEffect(() => {
    const interval = setInterval(() => {
      if (!portfolioLoading) {
        loadPortfolio();
      }
    }, PORTFOLIO_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadPortfolio, portfolioLoading]);

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <div className={styles.spinner} />
        <p>Loading your VC portal...</p>
      </div>
    );
  }

  return (
    <div className={styles.dashboardLayout}>
      <VCSidebar
        fundName={vcData?.step1_fund_info?.fundName}
        fundSize={vcData?.step1_fund_info?.fundSize}
      />
      <div className={styles.mainContent}>
        <Routes>
          <Route
            path="/"
            element={<Navigate to="/vc-dashboard/portfolio" replace />}
          />
          <Route 
            path="/portfolio" 
            element={
              <VCPortfolioPage 
                companies={portfolioCompanies}
                loading={portfolioLoading}
                error={portfolioError}
                onReload={loadPortfolio}
              />
            } 
          />
          <Route path="/updates" element={<VCUpdatesPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default VCDashboard;
